import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { plausibleEventClass } from '../lib/plausible';

interface LeaderboardTabsProps {
  weeks: number[];
  activeWeek: number;
  onChange: (week: number) => void;
  /** Per-week label override. Defaults to "Week N". Lets /rules render the
   *  post-contest Free Play tab (week 6) as "Free Play" instead of "Week 6". */
  labelFor?: (week: number) => string;
}

/**
 * Week-tab strip for the leaderboard panel. Active = solid red pill with a
 * purple→white-fade gradient stroke; inactive = grey-gradient pill with a
 * lighter grey-gradient stroke. Values lifted verbatim from Figma node
 * 238:540 / 238:543. We render the gradient strokes with the well-known
 * padding-box / border-box dual-background trick (transparent 1px border
 * lets the second background layer paint the border zone via a gradient),
 * which combines cleanly with `rounded-full` unlike `border-image`.
 *
 * Keyboard model: native button focus order + arrow-key navigation that
 * moves focus left/right and immediately selects. Home/End jump to the
 * first/last week.
 */

// Active fill: solid #FF0037 (255, 0, 55). Stroke gradient runs top→bottom
// per Figma node 238:540 — purple #AA15FF at the top edge fading to white
// at 38% opacity at the bottom edge.
const ACTIVE_BG =
  'linear-gradient(#FF0037, #FF0037) padding-box, ' +
  'linear-gradient(to bottom, #AA15FF 0%, rgba(255, 255, 255, 0.38) 100%) border-box';

// Inactive fill: bottom #8A8A8A@95% → top #8B8B8B; stroke: bottom #B1B1B1@45% → top #E8E8E8@72%.
const INACTIVE_BG =
  'linear-gradient(to top, rgba(138, 138, 138, 0.95) 0%, #8B8B8B 100%) padding-box, ' +
  'linear-gradient(to top, rgba(177, 177, 177, 0.45) 0%, rgba(232, 232, 232, 0.72) 100%) border-box';
export default function LeaderboardTabs({
  weeks,
  activeWeek,
  onChange,
  labelFor,
}: LeaderboardTabsProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusTab(index: number): void {
    const clamped = Math.max(0, Math.min(weeks.length - 1, index));
    const target = tabRefs.current[clamped];
    if (target) target.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, idx: number): void {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (idx + 1) % weeks.length;
      onChange(weeks[next]!);
      focusTab(next);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (idx - 1 + weeks.length) % weeks.length;
      onChange(weeks[prev]!);
      focusTab(prev);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(weeks[0]!);
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(weeks[weeks.length - 1]!);
      focusTab(weeks.length - 1);
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Leaderboard week"
      className="flex w-full items-center justify-center gap-[5px]"
    >
      {weeks.map((week, idx) => {
        const isActive = week === activeWeek;
        return (
          <button
            key={week}
            ref={(el) => {
              tabRefs.current[idx] = el;
            }}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls="leaderboard-list"
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(week)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={`flex h-[clamp(22px,3.2vw,32px)] flex-1 cursor-pointer items-center justify-center rounded-full border border-transparent transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${plausibleEventClass(
              'Leaderboard Tab Click',
              { week: String(week) },
            )}`}
            style={{ background: isActive ? ACTIVE_BG : INACTIVE_BG }}
          >
            <span className="font-[Orbitron] font-bold uppercase text-white text-[clamp(7px,2.1vw,10px)] tracking-[0.12em] md:text-[clamp(10px,1.2vw,12px)] md:tracking-[0.18em]">
              {labelFor ? labelFor(week) : `Week ${week}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
